/**
 * Migrated GWP implementation for current theme
 * Integrates with theme's cart system and web components
 */

// Cart API helper functions
const addLineItems = async (data) => {
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  
  if (!response.ok) {
    throw new Error('Failed to add items to cart');
  }
  
  const result = await response.json();
  
  // Dispatch cart change event for theme components
  const cartResponse = await fetch('/cart.js');
  const cart = await cartResponse.json();
  
  document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
    bubbles: true,
    detail: { cart, baseEvent: 'gwp:add' }
  }));

  // Force cart drawer refresh if it exists
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }
  
  return result;
};

const updateLineItems = async (data) => {
  const response = await fetch('/cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  
  if (!response.ok) {
    throw new Error('Failed to update cart items');
  }
  
  const result = await response.json();
  
  // Dispatch cart change event for theme components
  document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
    bubbles: true,
    detail: { cart: result, baseEvent: 'gwp:update' }
  }));

  // Force cart drawer refresh if it exists
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }
  
  return result;
};

// Helper function to trigger comprehensive cart refresh
const triggerCartRefresh = () => {
  // Update cart count components
  const cartCounts = document.querySelectorAll('cart-count');
  cartCounts.forEach(count => {
    const event = new CustomEvent('cart:refresh');
    count.dispatchEvent(event);
  });

  // Refresh cart drawer
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  // Update any other cart-related components
  document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
};

// Fetch GWP data from section
const fetchGiftWithPurchaseData = async () => {
  try {
    const response = await fetch('/cart?section_id=gift-with-purchase');
    if (!response.ok) {
      throw new Error('Failed to fetch GWP data');
    }
    
    const data = await response.text();
    const fragment = document.createRange().createContextualFragment(data);
    const scriptElement = fragment.querySelector('[data-gift-with-purchase]');
    
    if (scriptElement) {
      return JSON.parse(scriptElement.innerText);
    }
    
    return { gifts: [], giftsInCart: [] };
  } catch (error) {
    console.error('Error fetching GWP data:', error);
    return { gifts: [], giftsInCart: [] };
  }
};

export const checkGifts = async (giftDetails) => {
  if (!giftDetails) {
    // Fetch fresh data if not provided
    giftDetails = await fetchGiftWithPurchaseData();
  }

  const { gifts, giftsInCart } = giftDetails;
  const qualifyingGifts = gifts.filter((gift) => gift.qualifies);
  const manuallyRemovedGifts = JSON.parse(sessionStorage.getItem('manuallyRemovedGifts')) || [];

  try {
    /**
     * Remove any gifts that are invalid
     */
    const invalidGiftsInCart = giftsInCart.filter((giftInCart) => {
      const doesNotQualify = qualifyingGifts.every(
        (qualifyingGift) => qualifyingGift.product.id !== giftInCart.product_id
      );
      const hasMultipleQuantity = giftInCart.quantity > 1;
      return doesNotQualify || hasMultipleQuantity;
    });

    if (invalidGiftsInCart.length) {
      const updates = invalidGiftsInCart.reduce((_updates, _item) => {
        _updates[_item.key] = 0;
        return _updates;
      }, {});
      await updateLineItems({ updates });
      
      // Refresh cart after removing invalid gifts
      setTimeout(() => {
        triggerCartRefresh();
      }, 100);
      return;
    }

    /**
     * Add any gifts that qualify, aren't currently in the cart and haven't been
     * manually removed this session
     */
    const giftsToAdd = qualifyingGifts.filter((qualifyingGift) => {
      const isNotInCart = giftsInCart.every((giftInCart) => qualifyingGift.product.id !== giftInCart.product_id);
      const hasNotBeenManuallyRemoved = !manuallyRemovedGifts.includes(qualifyingGift.product.id);
      return isNotInCart && hasNotBeenManuallyRemoved;
    });

    if (giftsToAdd.length) {
      const items = giftsToAdd.map(({ product }) => {
        return {
          id: product.variants[0].id,
          quantity: 1,
          properties: {
            _gwp: true,
          },
        };
      });
      await addLineItems({ items });
      
      // Comprehensive cart refresh to ensure all components update
      setTimeout(() => {
        triggerCartRefresh();
      }, 100);
    }
  } catch (error) {
    console.error('Error processing gifts:', error);
  }
};

// Initialize GWP system
export const initializeGWP = () => {
  // Check gifts on page load
  document.addEventListener('DOMContentLoaded', async () => {
    await checkGifts();
  });

  // Check gifts when cart changes
  document.addEventListener('cart:change', async (event) => {
    // Avoid infinite loops by checking the source of the cart change
    if (event.detail.baseEvent !== 'gwp:add' && event.detail.baseEvent !== 'gwp:update') {
      await checkGifts();
    }
  });

  // Check gifts when cart is refreshed
  document.addEventListener('cart:refresh', async () => {
    await checkGifts();
  });

  // Handle manual gift removal
  document.addEventListener('click', (event) => {
    const removeButton = event.target.closest('[data-remove-gwp]');
    if (removeButton) {
      const productId = removeButton.getAttribute('data-remove-gwp');
      const manuallyRemovedGifts = JSON.parse(sessionStorage.getItem('manuallyRemovedGifts')) || [];
      
      if (!manuallyRemovedGifts.includes(parseInt(productId))) {
        manuallyRemovedGifts.push(parseInt(productId));
        sessionStorage.setItem('manuallyRemovedGifts', JSON.stringify(manuallyRemovedGifts));
      }
    }
  });

  // Clear manually removed gifts on new session
  window.addEventListener('beforeunload', () => {
    sessionStorage.removeItem('manuallyRemovedGifts');
  });
};

// Auto-initialize if this script is loaded
if (typeof window !== 'undefined') {
  initializeGWP();
}
